import { PaperClipIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import React, { useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { BtnComponent } from '../../components/button';
import MainPageDialog, { PageDialogContent, PageDialogFooter } from '../../components/dialog';
import { CheckBox } from '../../components/reactHookFormFields/checkbox';
import { IcdSearch } from '../../components/reactHookFormFields/icdSearch';
import { InputField } from '../../components/reactHookFormFields/inputField';
import { RadioInputField } from '../../components/reactHookFormFields/radio';
import { ReactSelectField } from '../../components/reactHookFormFields/selectField';
import { TextAreaField } from '../../components/reactHookFormFields/textAreaFields';
import { FiEdit, FiXCircle } from "react-icons/fi";
import { forEach } from 'lodash';
import { SectionHeader } from '../../components/section';
import { useGroupChatContext } from '../../context/groupchatContext';
import { useFileUploadMutation } from '../../services/chatService';
import { referralApi, useGetPatientProblemsQuery, useGetReferralsMetaQuery, useRequestReferralMutation } from '../../services/referralService';
import AttachmentContainer from '../groupChat/attachmentContainer';
import { useEffect } from 'react';
import SpeechMic from '../../components/mic';
import DwtPopup from '../../components/dwtNew/dwtPopUp';
import ActionMenuList from '../../components/ActionMenuList';
import NursingConsent from '../consent/nursingConsent';
import { AsyncSelectField } from '../../components/reactHookFormFields/asyncSelectField';
import AddReferring from './addReferring';
import { titleWithoutUnderscore } from '../../helpers/utility';
import moment from 'moment';

const IncomingReferral = ({cancelReferralPopup}) => {

    const consentType = [
        {option_id: 'upload_consents', title: 'Upload Consents'},
        {option_id: 'add_consents', title: 'Add Consent'},
        {option_id: 'pcc_consents', title: 'Consent Present in PCC'},
    ]

    const fileInputRef = useRef(null);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [isOpenConsentPopup, setIsOpenConsentPopup] = useState(false);
    const [isOpenAddReferringPopup, setIsOpenAddReferringPopup] = useState(false);
    const [showScannerPopup,setShowScannerPopup]=useState(false)
    const [addedConsent, setAddedConsent]=useState({})


    const {data: referralsMeta} = useGetReferralsMetaQuery();
    const [requestReferral, {isLoading}] = useRequestReferralMutation();

    const groupChatContext = useGroupChatContext();
    const {authData, selectedChat} = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
    }));


    const {data: problems} = useGetPatientProblemsQuery({pid: selectedChat.pid});

    const {
        handleSubmit,
        control,
        getValues,
        setValue,
        formState: { errors },
    } = useForm({defaultValues: {consent_method: consentType[0].option_id}});

    const authorizationVal = useWatch({control, name: 'authorization_required'});
    const consentMethodVal = useWatch({control, name: 'consent_method'});
    const reasonVal = useWatch({control, name: 'reason'});

    const { fields: referralIcd, append: appendIcdCodes } = useFieldArray({
        control: control,
        name: 'associated_icd_codes',
    });

    useEffect(()=>{
        setValue('provider_auth_no', '')
    }, [authorizationVal])

    useEffect(()=>{
        if(referralsMeta){
            setValue('urgency', {...referralsMeta?.referral_urgency[0], label: referralsMeta?.referral_urgency[0]?.title})
        }
    }, [referralsMeta])

    const [searchExternalProvider] = referralApi.endpoints.searchExternalProvider.useLazyQuery();
    const searchProvider = async (query) => {

        const response = await searchExternalProvider({ keyword: query, provider_type: 'referring' });
        const res = response?.data?.map((data) => {
            return data;
        });
    
        return new Promise(function (resolve, reject) {
          resolve(res || []);
        });
    };

    const [searchSpecialty] = referralApi.endpoints.searchSpecialty.useLazyQuery();
    const searchProviderSpecialty = async (query) => {

        const response = await searchSpecialty({ keyword: query});
        const res = response?.data?.map((data) => {
            return data;
        });
    
        return new Promise(function (resolve, reject) {
          resolve(res || []);
        });
    };


    const onSubmitForm = async(values) => {
        
        const payload = {
            referral_type: "incoming", pid: selectedChat?.pid,
            location_id: selectedChat?.location?.id,
            authorization_required: values.authorization_required,
            reason: reasonVal?.id == 'other' ? values?.other_reason : values.reason?.title,
            reason_slug: values.reason?.option_id,
            other_reason: values?.other_reason,
            service_request_group1: values.service_request_group1, clinical_notes: values.clinical_notes,
            provider_auth_no: values.provider_auth_no, urgency: values.urgency?.option_id,
            reconcile_data: {
                problem_data: values.associated_icd_codes
            },
            referring_by_name: values.referring_by_name?.name,
            referring_by_id: values.referring_by_name?.id,
            consent_in_pcc: values.consent_method == 'pcc_consents' ? true : false
        };

        let service_request_group2 = []
        if(values?.surgery){
            service_request_group2.push("surgery")
        }
        if(values?.diagnostic_testing){
            service_request_group2.push("diagnostic_testing")
        }
        payload.service_request_group2 = service_request_group2.join(",")

        let service_request_group3 = []
        if(values?.behavioral_health){
            service_request_group3.push("behavioral_health")
        }
        if(values?.wound_care){
            service_request_group3.push("wound_care")
        }
        if(values?.capacity_evaluation){
            service_request_group3.push("capacity_evaluation")
        }
        payload.service_request_group3 = service_request_group3.join(",")
        
        if(values?.referring_speciality){
            payload.referral_provider_speciality = values?.referring_speciality.id;
            payload.referral_provider_speciality_title = values?.referring_speciality.title;
        }

        if(attachedFiles?.length){
            payload.files = attachedFiles.map((item)=> ({...item, uuid: item.attachment_id}));
        }
        
        const res = await requestReferral(payload);
        if(res?.data?.code === 0){
            toast.success(res.data.msg);
            cancelReferralPopup()
        }
        else {
            toast.error(res?.data?.msg);
        }
    }

    const [fileUpload, { isLoading: isUploadingFile } ] = useFileUploadMutation()
    const fileUploadFunction=(formData,type)=>{
        fileUpload(formData).then((res) => {
            if(res?.data?.code === 0){
                setAttachedFiles(prevAttachment => {
                    const newArray = [...prevAttachment, res.data.data];
                    return newArray;
                });
                if (type=="scanner"){
                    setShowScannerPopup(false)
                }
            }
            else {
                toast.error(res?.data?.msg);
            }
        });
    }

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        forEach(files, (file)=>{
            const formData = new FormData();
            formData.append('file', file);
            fileUploadFunction(formData)
        })
    };

    const handleTriggerAttachment = () => {
        fileInputRef.current.click();
    }

    const onRemoveFile = (index) => {
        setAttachedFiles(attachedFiles.filter((_, i) => i !== index))
    }

    const hanldeICDChange = () => {
        const val = getValues('associated_icd');
        if(val){
            const foundICDIndex = referralIcd.findIndex((item)=> item.code == val.code);
            if(foundICDIndex == -1){
                appendIcdCodes(val);
            }
            else{
                toast.error("Diagnosis already added.")
            }
            setValue('associated_icd', '')
        }
    }

    const removeAssociatedCode = (codeKey, index) => {
        let codes = getValues(codeKey);
        codes.splice(index, 1);
        setValue(codeKey, [...codes]);
    }

    const onSpeechAvailable = (text) =>{
        const notesVal = getValues('clinical_notes');
        const finalText = ((notesVal ? notesVal : '')+' '+text).trim()
        setValue('clinical_notes', finalText)
    }

    const onCloseAddReferringAfterSave = (data) => {
        if(data?.id){
            setValue('referring_by_name', {id: data.id, name: data.fname+' '+data.lname, phonecell: data.phonecell, email: data.email})
        }
        setIsOpenAddReferringPopup(false)
    }

    const onCloseAddConsentAfterSave = (data) => {
        if(data?.id){
            setAddedConsent(data)
        }
        setIsOpenConsentPopup(false)
    }

    return (
        <>
            <PageDialogContent contentCls="p-3 sm:p-4 md:px-6">
                <form id="incoming_referral_form" onSubmit={handleSubmit(onSubmitForm)} className="min-w-0">
                    {authData?.is_psych ?
                        null
                        :
                        <div className="flex items-start mb-4">
                            <RadioInputField
                                control={control}
                                title="Authorization Required"
                                spaceX="4"
                                name="authorization_required"
                                data={[{ key: "0", label: "No" }, { key: "1", label: "Yes" }]}
                            />
                        </div>
                    }
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-4">
                        <div className="min-w-0">
                            <ReactSelectField
                                title="Reason"
                                name="reason"
                                control={control}
                                optionData={referralsMeta?.reasons?.map((v, k) => ({
                                    ...v,
                                    label: v.title,
                                }))}
                                rules={{ required: { value: true } }}
                                errors={errors}
                                menuPlacement="bottom"
                            />
                        </div>
                        {reasonVal?.id == "other" ? (
                            <div className="min-w-0">
                                <InputField
                                    title="Other Reason"
                                    name="other_reason"
                                    control={control}
                                    rules={{ required: { value: true } }}
                                    errors={errors}
                                />
                            </div>
                        ) : null}
                        <div className="min-w-0">
                            <ReactSelectField
                                title="Urgency"
                                name="urgency"
                                control={control}
                                optionData={referralsMeta?.referral_urgency?.map((v, k) => ({
                                    ...v,
                                    label: v.title,
                                }))}
                                menuPlacement="bottom"
                            />
                        </div>
                        {authorizationVal == 1 ? (
                            <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                                <InputField
                                    title="PAN / Auth. No"
                                    name="provider_auth_no"
                                    control={control}
                                    rules={{ required: { value: true } }}
                                    errors={errors}
                                />
                            </div>
                        ) : null}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 mb-4">
                        <div className="flex-1 min-w-0">
                            <AsyncSelectField
                                showLabel="title"
                                title="Specialty"
                                name="referring_speciality"
                                control={control}
                                search={(query) => searchProviderSpecialty(query)}
                                rules={{ required: { value: true } }}
                                errors={errors}
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <AsyncSelectField
                                showLabel="name"
                                title="Referred By"
                                name="referring_by_name"
                                control={control}
                                search={(query) => searchProvider(query)}
                            />
                        </div>
                        <div className="flex items-center sm:items-end pb-1 sm:pb-0">
                            <button type="button" aria-label="Add Referring" onClick={() => setIsOpenAddReferringPopup(true)} className="shrink-0">
                                <PlusCircleIcon className="h-8 w-8 sm:h-6 sm:w-6 text-green-600 cursor-pointer" />
                            </button>
                        </div>
                    </div>
                    <div className="mb-5 min-w-0">
                        <SectionHeader title="Services Requested" />
                        {authData?.is_psych ? null : (
                            <div className="pt-2 pb-1 flex flex-col md:flex-row gap-4">
                                <div className="border rounded pl-1 pr-2 min-w-0 overflow-x-auto">
                                    <RadioInputField
                                        control={control}
                                        title=""
                                        spaceX="4"
                                        id_key="option_id"
                                        name="service_request_group1"
                                        data={referralsMeta?.referral_service_type_group1 ? referralsMeta?.referral_service_type_group1 : []}
                                    />
                                </div>
                                <div className="border rounded flex flex-wrap gap-2 min-w-0">
                                    {referralsMeta?.referral_service_type_group2?.map((item) => (
                                        <div key={item?.option_id} className="p-1">
                                            <CheckBox
                                                font="text-md"
                                                control={control}
                                                name={item?.option_id}
                                                title={item.title}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex flex-wrap pt-1 gap-2 min-w-0">
                            {referralsMeta?.referral_service_type_group3?.map((item) => (
                                <div key={item?.option_id} className="p-1">
                                    <CheckBox
                                        font="text-md"
                                        control={control}
                                        name={item?.option_id}
                                        title={item.title}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mb-5 min-w-0">
                        <SectionHeader title="Diagnosis" />
                        <div className="pt-2 pb-1 flex flex-col min-w-0">
                            <IcdSearch
                                title="Diagnosis"
                                id="associated_icd"
                                name="associated_icd"
                                optionData={problems}
                                control={control}
                                onChange={(e) => {
                                    hanldeICDChange();
                                }}
                            />
                        </div>
                        <div className="pt-1 min-w-0">
                            {referralIcd.map((val, valIndex) => (
                                <div className="col-span-12 flex justify-between items-center gap-2 cursor-pointer teal-c-900 chief-border-left active-prob shadow-md mb-1 min-height-45 bg-blue-ecf px-2 min-w-0" key={`icd-${valIndex}`}>
                                    <span className="py-1 px-2 truncate flex-1 min-w-0" title={`${val?.code} - ${val?.short_desc || val?.name}`}>
                                        {`${val?.code} - ${val?.short_desc || val?.name}`}
                                    </span>
                                    <button type="button" aria-label="Remove" className="ml-2 cursor-pointer text-amber-700 shrink-0 p-1" onClick={() => removeAssociatedCode("associated_icd_codes", valIndex)}>
                                        <FiXCircle />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                    {showScannerPopup && <DwtPopup showScannerPopup={showScannerPopup} fileUploadFunction={fileUploadFunction} setFileFromScanner={setAttachedFiles} setShowScannerPopup={setShowScannerPopup} />}

                    <div className="mb-5 min-w-0">
                        <SectionHeader
                            title="Consents"
                            content={
                                <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-start sm:items-center">
                                    <RadioInputField
                                        control={control}
                                        title=""
                                        spaceX="4"
                                        id_key="option_id"
                                        name="consent_method"
                                        data={consentType}
                                    />
                                    <div className="w-8 flex align-middle shrink-0 self-end ">
                                        {consentMethodVal == 'upload_consents' ?
                                            <>
                                                <ActionMenuList
                                                    icon={<PaperClipIcon className="h-4 w-4 text-gray-400 cursor-pointer" />}
                                                    title=""
                                                    position='up'
                                                    menuList={[
                                                    {
                                                        label: "Upload Document",
                                                        onClick: () => handleTriggerAttachment(),
                                                    },
                                                    {
                                                        label: "Scan Document",
                                                        onClick: () => {
                                                            setShowScannerPopup(true)
                                                        },
                                                    },
                                                    ]}
                                                />
                                                {/* <BtnComponent btnType='default' size="xs" type="button" onClick={()=>setShowScannerPopup(true)} text="Scan Document" />
                                                <BtnComponent btnType='default' size="xs" type="button" onClick={handleTriggerAttachment} text="Add" /> */}
                                                <input
                                                    id="fileInput"
                                                    type="file"
                                                    ref={fileInputRef}
                                                    className="hidden"
                                                    onChange={handleFileChange}
                                                    multiple
                                                />
                                            </>
                                            : null
                                        }
                                        {(consentMethodVal == 'add_consents' && !addedConsent?.id) ?
                                            <BtnComponent btnType='default' size="xs" type="button" onClick={()=>setIsOpenConsentPopup(true)} text="Add" />
                                        : null}
                                    </div>
                                </div>
                            }
                        />
                        <div className="pt-2 pb-1 flex flex-col min-w-0">
                            {consentMethodVal == "upload_consents" &&
                                <>
                                    {(!attachedFiles?.length && !isUploadingFile) ? 
                                        <div className='bg-gray p-1 shadow-sm w-full'>No consent uploaded</div>
                                        : null
                                    }
                                    <AttachmentContainer data={{files:attachedFiles}} onRemoveFile={onRemoveFile} isLoading={isUploadingFile} />
                                </>
                            }
                            {consentMethodVal == 'add_consents' &&
                                <div className='bg-gray p-1 shadow-sm w-full'>
                                    {addedConsent?.id ? (
                                        <span className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-center justify-center text-center sm:text-left">
                                            <span>Consent is given by <span className="capitalize">{titleWithoutUnderscore(addedConsent.taken_from)}</span> on {moment(addedConsent.created).format("MM/DD/YYYY")}</span>
                                            <button type="button" aria-label="Edit consent" className="cursor-pointer shrink-0" onClick={() => setIsOpenConsentPopup(true)}>
                                                <FiEdit className="cursor-pointer" />
                                            </button>
                                        </span>
                                    ) : (
                                        <span>No consent added</span>
                                    )}
                                </div>
                            }
                            {consentMethodVal == 'pcc_consents' &&
                                <div className='bg-gray p-1 shadow-sm w-full'>
                                    {/* It is verified that patient's consent exists in PCC */}
                                    Consent is present in PCC. Confirmed by <b>{authData.name}</b> on <b>{moment().format('MM/DD/YYYY')}</b>
                                </div>
                            }
                        </div>
                    </div>
                    <div className="mb-2 relative min-w-0">
                        <SpeechMic transformSpeech={onSpeechAvailable} />
                        <TextAreaField
                            name="clinical_notes"
                            title="Comments"
                            // data-audio-enabled={"true"}
                            rows="4"
                            control={control}
                        />
                    </div>
                </form>
            </PageDialogContent>
            <PageDialogFooter>
                <BtnComponent text="Cancel" btnType="default" onClick={cancelReferralPopup} />
                <BtnComponent text="Request Referral" btnType='primary_theme' loading={isLoading} form="incoming_referral_form" type="submit" />
            </PageDialogFooter>

            {isOpenConsentPopup ?
                <MainPageDialog
                    title="Consent"
                    onCancelHandler={() => setIsOpenConsentPopup(false)}
                    modalSize="md"
                    maxHeight={true}
                    component={
                        <NursingConsent
                            data={addedConsent}
                            cancelNursingConsentPopup={(data) => onCloseAddConsentAfterSave(data)}
                        />
                    }
                    footer={false}
                >
                </MainPageDialog>
                : null
            }

            {isOpenAddReferringPopup ?
                <MainPageDialog
                    title="Add Referring"
                    onCancelHandler={() => setIsOpenAddReferringPopup(false)}
                    modalSize="md"
                    maxHeight={true}
                    component={
                        <AddReferring
                            cancelAddReferringPopup={(data) => onCloseAddReferringAfterSave(data)}
                        />
                    }
                    footer={false}
                >
                </MainPageDialog>
                : null
            }
        </>
    );
};

export default IncomingReferral;
